import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import { getColorObjectsStyleByRecord } from 'PDF/helpers/documentStyle';

export type IInfoObject = { value: string; super?: string };

type IStripedTableRowInfo = string | IInfoObject;
export interface IStripedTableRow {
  id: string;
  heading: string;
  imgSrc: string;
  info: IStripedTableRowInfo[];
  imgValueSettings?: {
    height: string;
  };
}

export interface IStripedTableProps {
  rows: IStripedTableRow[];
  fixedRows?: number[];
}

const colorIndexes = {
  green: [2, 12, 30],
  blue: [2, 5, 30],
  red: [2, 5, 30],
  blueGreen: [2, 38, 35],
  blueRed: [2, 38, 11],
  blueYellow: [2, 38, 39],
};

export const StripedTable = ({ rows, fixedRows = [] }: IStripedTableProps) => {
  const [backgroundColor, textColor, fixedColBgColor] = getColorObjectsStyleByRecord(colorIndexes, [
    'backgroundColor',
    'color',
    'backgroundColor',
  ]);

  const renderValue = (item: IStripedTableRowInfo, imgValueSettings: IStripedTableRow['imgValueSettings']) => {
    if (typeof item === 'string') {
      if (imgValueSettings && item) {
        return <Image src={item} style={[styles.logo, { height: imgValueSettings.height }]} />;
      }

      return <Text style={styles.text}>{item}</Text>;
    }

    return (
      <Text style={styles.text}>
        {item.value}
        {item?.super && <Text style={styles.super}>{item.super}</Text>}
      </Text>
    );
  };

  const renderRow = ({ info, heading, imgSrc, id, imgValueSettings }: IStripedTableRow, index: number) => {
    const isThreeCols = info.length === 3;
    const isFixedRow = fixedRows.includes(index);

    const rowStyle = {
      marginBottom: '0px',
      ...(isFixedRow ? fixedColBgColor : index % 2 === 0 ? backgroundColor : { backgroundColor: '#fff' }),
      padding: `8px ${isThreeCols ? 22 : 10}px 7.1px 8.65px`,
    };

    return (
      <>
        <View key={id} style={[rowStyle, styles.rowWrap]} fixed={isFixedRow}>
          <View style={[styles.headingWrap, { width: isThreeCols ? 100 : 164 }]}>
            <Image src={imgSrc} style={styles.img} />
            <Text style={[styles.heading, textColor]}>{heading}</Text>
          </View>
          <View style={[styles.infoWrap, { gap: isThreeCols ? 30 : 20 }]}>
            {info.map((item, index) => {
              return (
                <View key={index} style={{ width: `${100 / info.length}%` }}>
                  {renderValue(item, imgValueSettings)}
                </View>
              );
            })}
          </View>
        </View>
      </>
    );
  };

  return <>{rows.map(renderRow)}</>;
};

const styles = StyleSheet.create({
  rowWrap: {
    display: 'flex',
    flexDirection: 'row',
    gap: 20,
  },
  headingWrap: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  img: {
    width: '8px',
    height: '8px',
    marginRight: '8px',
    marginTop: '2px',
  },
  heading: {
    fontFamily: 'Noto',
    fontWeight: 600,
    fontSize: '9px',
    maxWidth: '100px',
  },
  infoWrap: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
  },
  text: {
    fontFamily: 'Noto',
    fontWeight: 300,
    fontSize: '9px',
    color: '#212121',
  },
  logo: {
    maxWidth: 103,
    maxHeight: 80,
    objectFit: 'contain',
    objectPositionX: 0,
    objectPositionY: 0,
  },
  super: {
    verticalAlign: 'super',
  },
});
